import React, { useEffect } from 'react';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { logout as firebaseLogout } from '@/lib/firebase';
import { Heading } from '@/components/catalyst/heading';
import { Text, Strong } from '@/components/catalyst/text';
import { Button } from '@/components/catalyst/button';

interface UnauthorizedProps {
  email?: string;
  reason?: 'invalid_domain' | 'not_in_allowed_groups' | 'unknown';
}

const Unauthorized: React.FC<UnauthorizedProps> = ({ email, reason = 'unknown' }) => {
  useEffect(() => {
    // Forza logout al mount per sicurezza
    const performLogout = async () => {
      try {
        await firebaseLogout();
      } catch (error) {
        console.error('Errore durante il logout:', error);
      }
    };
    performLogout();
  }, []);

  const messages = {
    invalid_domain: {
      title: 'Dominio Non Autorizzato',
      description: 'Il tuo account non appartiene a un dominio autorizzato.',
      detail: 'Solo gli account @polo9.org possono accedere a questo sistema.'
    },
    not_in_allowed_groups: {
      title: 'Accesso Negato',
      description: 'Il tuo account non è autorizzato ad accedere a questo sistema.',
      detail: 'Contatta l\'amministratore per richiedere l\'accesso.'
    },
    unknown: {
      title: 'Accesso Non Autorizzato',
      description: 'Non hai i permessi necessari per accedere.',
      detail: 'Se ritieni che sia un errore, contatta il supporto.'
    }
  };

  const message = messages[reason];

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-50 px-4 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mb-4">
            <img 
              src="/logo_simbolo.png" 
              alt="Polo9 Logo" 
              className="mx-auto h-12 w-12 opacity-90 dark:opacity-60"
            />
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-xl shadow-zinc-900/5 dark:border-white/10 dark:bg-zinc-900 dark:shadow-black/40">
          {/* Icona */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/10">
              <ShieldExclamationIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Titolo */}
          <div className="text-center mb-6">
            <Heading className="mb-2">{message.title}</Heading>
            <Text>{message.description}</Text>
          </div>

          {/* Email utente */}
          {email && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-500/10 dark:bg-red-500/5">
              <Text className="text-center font-mono text-sm !text-red-700 dark:!text-red-300">
                {email}
              </Text>
            </div>
          )}

          {/* Dettaglio */}
          <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-white/5 dark:bg-zinc-800">
            <Text className="text-center text-sm !text-zinc-700 dark:!text-zinc-300">
              {message.detail}
            </Text>
          </div>

          {/* Contatti */}
          <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-white/5 dark:bg-zinc-800">
            <Text className="mb-2 text-sm !text-zinc-700 dark:!text-zinc-300">
              <Strong className="text-zinc-900 dark:text-white">Hai bisogno di accesso?</Strong>
            </Text>
            <Text className="text-xs !text-zinc-700 dark:!text-zinc-300">
              Contatta: <Strong className="font-mono !text-zinc-900 dark:!text-white">it@polo9.org</Strong>
            </Text>
          </div>

          {/* Button */}
          <Button
            onClick={() => window.location.href = '/'}
            className="w-full"
          >
            Torna alla Pagina di Login
          </Button>

          <Text className="mt-4 text-center text-xs text-zinc-500 dark:text-zinc-400">
            Verrai disconnesso automaticamente per motivi di sicurezza
          </Text>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Text className="text-xs">
            Nexus · Accesso riservato
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
